// ─────────────────────────────────────────────────────────────────────────
// exportCostControlPdf(reportData, options)
//
// Orchestration ONLY. This is the single entry point CostControl.jsx calls.
// It never computes a financial figure and never re-derives anything
// buildReport() already produced — reportData is passed straight through,
// unchanged, to buildCostControlPdfModel().
//
// Responsibilities:
//   1. Resolve the company logo (org.logo_url if present, else the
//      built-in CostSpace default the renderer already ships with).
//   2. Read the project's saved Cost Control Report Configuration
//      (cost_control_report_config — set via Release Management's
//      "Reports" tab) and merge it in as plain display metadata: logo
//      override, cover page toggle, client/consultant names, website,
//      footer text, confidential label, print-date/generated-by toggles.
//      This is a passthrough only — zero financial data lives in this
//      table, and this file performs no calculation on any of it.
//   3. Fetch monthly.js's buildMonthlyActualCostData(projectId) — the
//      EXACT SAME shared source Executive Report already reads its own
//      "This Period" (Movement) figures from — and pass it straight
//      through to buildCostControlPdfModel(). This file performs no
//      calculation on it whatsoever; it is a single shared source of
//      truth between Executive Report and this PDF, matched by `code`
//      inside buildCostControlPdfModel()'s own pickDisplayFields(), same
//      as Executive Report's `monthly.byCostCode[r.code]?.movement` /
//      `monthly.grandMovement` lookups.
//   4. Wire buildCostControlPdfModel() → renderCostControlPdf() → open a
//      print-ready window, opened synchronously before any await so
//      browsers don't treat it as a blocked popup.
//
// Explicit values passed in `options` by the caller (CostControl.jsx) take
// priority over the saved config for any field the caller already
// supplies directly (e.g. includeCoverPage passed explicitly for a
// single-tab Print Screen export) — the saved config only fills in
// fields the caller hasn't already decided.
// ─────────────────────────────────────────────────────────────────────────

import { supabase } from './supabase'
import { buildCostControlPdfModel } from './buildCostControlPdfModel'
import { renderCostControlPdf } from './CostControlPdfRenderer'
import { buildMonthlyActualCostData } from './monthlyActualCost'

async function resolveLogoUrl(options) {
  if (options.logoUrl) return options.logoUrl
  if (!options.orgId) return null
  try {
    const { data } = await supabase.from('organizations').select('logo_url').eq('id', options.orgId).single()
    return data?.logo_url || null
  } catch {
    return null
  }
}

async function loadReportConfig(projectId) {
  if (!projectId) return null
  try {
    const { data } = await supabase
      .from('cost_control_report_config')
      .select('*')
      .eq('project_id', projectId)
      .maybeSingle()
    return data || null
  } catch {
    return null
  }
}

export async function exportCostControlPdf(reportData, options = {}) {
  // Open the window synchronously (before any await) so browsers don't
  // treat it as a blocked popup — same pattern as the existing
  // exportReport() Excel/HTML export.
  const win = window.open('', '_blank')

  const [logoUrl, reportConfig, fetchedMonthlyActualCost] = await Promise.all([
    resolveLogoUrl(options),
    loadReportConfig(options.projectId),
    // Only fetched here as a fallback — if the caller (CostControl.jsx)
    // already has monthlyActualCost loaded (the same object its own
    // Executive Cards/Summary rows are already displaying), it is passed
    // straight through via options.monthlyActualCost below and this fetch
    // is skipped entirely, so the screen and the PDF are guaranteed to
    // read the exact same object rather than two independently-fetched
    // (and potentially diverging) copies of the same data.
    (!options.monthlyActualCost && options.projectId) ? buildMonthlyActualCostData(options.projectId) : Promise.resolve(null),
  ])
  const monthlyActualCost = options.monthlyActualCost || fetchedMonthlyActualCost

  const model = buildCostControlPdfModel(reportData, {
    ...options,
    logoUrl: options.logoUrl || reportConfig?.logo_url || logoUrl,
    includeCoverPage: options.includeCoverPage ?? reportConfig?.show_cover_page ?? true,
    clientName: options.clientName ?? reportConfig?.client_name ?? '',
    consultantName: options.consultantName ?? reportConfig?.consultant_name ?? '',
    orgWebsite: options.orgWebsite ?? reportConfig?.org_website ?? '',
    footerText: options.footerText ?? reportConfig?.footer_text ?? '',
    confidentialLabel: options.confidentialLabel ?? reportConfig?.confidential_label ?? 'Strictly Confidential — Top Management Only',
    showPrintDate: options.showPrintDate ?? reportConfig?.show_print_date ?? true,
    showGeneratedBy: options.showGeneratedBy ?? reportConfig?.show_generated_by ?? true,
    monthlyActualCost,
  })

  const html = renderCostControlPdf(model)

  if (win) {
    win.document.write(html)
    win.document.close()
    // Give the browser a beat to load fonts/logo image before the user
    // triggers Print — no auto-print call here, left to the person
    // reviewing the report (matches existing exportReport() behavior).
  }

  return model
}

